import { useState, useEffect, useCallback } from "react";
import Table from "@amzn/awsui-components-react/polaris/table";
import Header from "@amzn/awsui-components-react/polaris/header";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Box from "@amzn/awsui-components-react/polaris/box";
import TextFilter from "@amzn/awsui-components-react/polaris/text-filter";
import Select from "@amzn/awsui-components-react/polaris/select";
import StatusIndicator from "@amzn/awsui-components-react/polaris/status-indicator";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import Textarea from "@amzn/awsui-components-react/polaris/textarea";
import Input from "@amzn/awsui-components-react/polaris/input";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Badge from "@amzn/awsui-components-react/polaris/badge";
import Link from "@amzn/awsui-components-react/polaris/link";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { evalApi } from "../../client/EvalApiClient";

// ─── Types ──────────────────────────────────────────────────────────────────

interface GoldenCandidate {
  candidate_id: string;
  created_at: string;
  status: string;
  case_key: string;
  input_prompt: string;
  expected_behavior: string;
  root_cause: string;
  source_evaluator: string;
  source_session_id: string;
  source_run_id: string;
  suggested_suite: string;
  suggested_metrics: string[];
  confidence: string;
  evidence_s3_key: string;
}

// ─── API Helpers ────────────────────────────────────────────────────────────

async function listCandidates(status?: string): Promise<{ items: GoldenCandidate[]; count: number }> {
  const params = status ? `?status=${status}` : "";
  return evalApi.signedRequest("GET", `/golden-candidates${params}`);
}

async function getCandidate(id: string): Promise<{ candidate: GoldenCandidate; evidence_url?: string }> {
  return evalApi.signedRequest("GET", `/golden-candidates/${encodeURIComponent(id)}`);
}

async function approveCandidate(id: string, body: Record<string, unknown>): Promise<{ status: string; golden_dataset_test_case_id: string; suite: string }> {
  return evalApi.signedRequest("POST", `/golden-candidates/${encodeURIComponent(id)}/approve`, JSON.stringify(body));
}

async function rejectCandidate(id: string, reason?: string): Promise<{ status: string }> {
  return evalApi.signedRequest("POST", `/golden-candidates/${encodeURIComponent(id)}/reject`, JSON.stringify({ reason: reason || "" }));
}

// ─── Constants ──────────────────────────────────────────────────────────────

// Labels are translated in the component (candidates.status.<key>).
const STATUS_OPTIONS = [
  { key: "all", value: "__all__" },
  { key: "pending", value: "pending" },
  { key: "approved", value: "approved" },
  { key: "rejected", value: "rejected" },
] as const;

// ─── Component ──────────────────────────────────────────────────────────────

export default function GoldenCandidatesPage() {
  const { t } = useTranslation("eval");
  const navigate = useNavigate();
  const statusOptions = STATUS_OPTIONS.map((o) => ({ label: t(`candidates.status.${o.key}`), value: o.value }));
  const [candidates, setCandidates] = useState<GoldenCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState<GoldenCandidate[]>([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [filterText, setFilterText] = useState("");
  const [detailItem, setDetailItem] = useState<GoldenCandidate | null>(null);
  const [evidenceUrl, setEvidenceUrl] = useState<string | null>(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [editExpected, setEditExpected] = useState("");
  const [editSuite, setEditSuite] = useState("");
  const [editMetrics, setEditMetrics] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  // Toasts are short-lived, so their text is built when they are shown.
  const errorText = (err: unknown) => (err instanceof Error ? err.message : t("candidates.unknown"));
  const showToast = (type: "success" | "error", msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const status = statusFilter === "__all__" ? undefined : statusFilter;
      const data = await listCandidates(status);
      setCandidates(data.items || []);
    } catch (err) {
      console.error("Failed to load candidates", err);
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    listCandidates("pending").then((d) => setPendingCount(d.count || 0)).catch(() => {});
  }, []);

  const filtered = candidates.filter((c) => {
    if (!filterText) return true;
    const q = filterText.toLowerCase();


  return (
      (c.input_prompt || "").toLowerCase().includes(q) ||
      (c.source_evaluator || "").toLowerCase().includes(q) ||
      (c.case_key || "").toLowerCase().includes(q)
    );
  });

  const [pendingCount, setPendingCount] = useState(0);

  const openDetail = async (item: GoldenCandidate) => {
    setDetailItem(item);
    setEditPrompt(item.input_prompt || "");
    setEditExpected(item.expected_behavior || "");
    setEditSuite(item.suggested_suite || "");
    setEditMetrics((item.suggested_metrics || []).join(", "));
    setEvidenceUrl(null);
    try {
      const detail = await getCandidate(item.candidate_id);
      if (detail.evidence_url) setEvidenceUrl(detail.evidence_url);
    } catch { /* ignore */ }
  };

  const handleApprove = async () => {
    if (!detailItem) return;
    setSubmitting(true);
    try {
      const result = await approveCandidate(detailItem.candidate_id, {
        input_prompt: editPrompt,
        expected_output: editExpected,
        suite: editSuite,
        metrics: editMetrics.split(",").map((m) => m.trim()).filter(Boolean),
      });
      showToast("success", t("candidates.approved", { id: result.golden_dataset_test_case_id }));
      setDetailItem(null);
      await loadData();
    } catch (err) {
      showToast("error", t("candidates.approveFailed", { error: errorText(err) }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!detailItem) return;
    setSubmitting(true);
    try {
      await rejectCandidate(detailItem.candidate_id, "Rejected via portal");
      showToast("success", t("candidates.rejected"));
      setDetailItem(null);
      await loadData();
    } catch (err) {
      showToast("error", t("candidates.rejectFailed", { error: errorText(err) }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkApprove = async () => {
    setSubmitting(true);
    try {
      let count = 0;
      for (const item of selectedItems) {
        if (item.status !== "pending") continue;
        await approveCandidate(item.candidate_id, {
          input_prompt: item.input_prompt,
          expected_output: item.expected_behavior,
          suite: item.suggested_suite,
          metrics: item.suggested_metrics,
        });
        count++;
      }
      showToast("success", t("candidates.bulkApproved", { n: count }));
      setSelectedItems([]);
      await loadData();
    } catch (err) {
      showToast("error", t("candidates.bulkApproveFailed", { error: errorText(err) }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkReject = async () => {
    setSubmitting(true);
    try {
      let count = 0;
      for (const item of selectedItems) {
        if (item.status !== "pending") continue;
        await rejectCandidate(item.candidate_id, "Bulk rejected via portal");
        count++;
      }
      showToast("success", t("candidates.bulkRejected", { n: count }));
      setSelectedItems([]);
      await loadData();
    } catch (err) {
      showToast("error", t("candidates.bulkRejectFailed", { error: errorText(err) }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: "0" }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8 }}>
          <a onClick={() => navigate("/eval/datasets")} style={{ fontSize: 13, color: "#0073bb", cursor: "pointer", textDecoration: "none" }}>{t("candidates.back")}</a>
        </div>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: "#0f141a", margin: "0 0 4px" }}>
          {t("candidates.title")}
        </h1>
        <p style={{ fontSize: 14, color: "#414d5c", margin: 0 }}>
          {t("candidates.subtitle")}
        </p>
      </div>

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e9ebed", padding: 24 }}>
        <Header
          variant="h2"
          counter={`(${filtered.length})`}
          description={pendingCount > 0 ? t("candidates.awaiting", { n: pendingCount }) : t("candidates.allReviewed")}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={loadData} iconName="refresh">{t("candidates.refresh")}</Button>
              <Button disabled={selectedItems.length === 0 || selectedItems.some((i) => i.status !== "pending")} onClick={handleBulkReject}>{t("candidates.rejectSelected")}</Button>
              <Button variant="primary" disabled={selectedItems.length === 0 || selectedItems.some((i) => i.status !== "pending")} onClick={handleBulkApprove}>{t("candidates.approveSelected")}</Button>
            </SpaceBetween>
          }
        >
          {t("candidates.header")}
        </Header>

        <div style={{ display: "flex", gap: 12, marginBottom: 16, marginTop: 16 }}>
          <Select
            selectedOption={statusOptions.find((o) => o.value === statusFilter) || statusOptions[0]}
            onChange={({ detail }) => setStatusFilter(detail.selectedOption.value || "__all__")}
            options={statusOptions}
          />
          <div style={{ flex: 1 }}>
            <TextFilter
              filteringText={filterText}
              onChange={({ detail }) => setFilterText(detail.filteringText)}
              filteringPlaceholder={t("candidates.search")}
            />
          </div>
        </div>

        <Table
          columnDefinitions={[
            {
              id: "input_prompt",
              header: t("candidates.col.prompt"),
              cell: (item) => (
                <Link variant="primary" onFollow={(e) => { e.preventDefault(); openDetail(item); }}>
                  {(item.input_prompt || t("candidates.emptyPrompt")).length > 60 ? (item.input_prompt || "").slice(0, 60) + "..." : item.input_prompt || t("candidates.emptyPrompt")}
                </Link>
              ),
            },
            {
              id: "evaluator",
              header: t("candidates.col.evaluator"),
              cell: (item) => <Badge color="blue">{(item.source_evaluator || "").replace("Deval_", "").replace("Builtin.", "")}</Badge>,
            },
            {
              id: "confidence",
              header: t("candidates.col.confidence"),
              cell: (item) => {
                const conf = parseFloat(item.confidence || "0");


  return (
                  <span style={{ fontWeight: 600, color: conf >= 0.8 ? "#00802f" : conf >= 0.6 ? "#d97706" : "#db0000" }}>
                    {(conf * 100).toFixed(0)}%
                  </span>
                );
              },
            },
            {
              id: "suite",
              header: t("candidates.col.suite"),
              cell: (item) => (item.suggested_suite || "").replace("online-feedback-", ""),
            },
            {
              id: "date",
              header: t("candidates.col.date"),
              cell: (item) => (item.created_at || "").slice(0, 10),
            },
            {
              id: "status",
              header: t("candidates.col.status"),
              cell: (item) => {
                const type = item.status === "approved" ? "success" : item.status === "rejected" ? "error" : "pending";
                return <StatusIndicator type={type}>{item.status}</StatusIndicator>;
              },
            },
          ]}
          items={filtered}
          loading={loading}
          loadingText={t("candidates.loading")}
          selectionType="multi"
          selectedItems={selectedItems}
          onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
          trackBy="candidate_id"
          empty={
            <Box textAlign="center" color="inherit">
              <b>{t("candidates.noCandidates")}</b>
              <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                {t("candidates.noCandidatesBody")}
              </Box>
            </Box>
          }
        />
      </div>

      {/* Detail / Approve Modal */}
      <Modal
        visible={!!detailItem}
        onDismiss={() => setDetailItem(null)}
        header={t("candidates.reviewTitle")}
        size="max"
        footer={
          detailItem?.status === "pending" ? (
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                
                <Button variant="normal" loading={submitting} onClick={handleReject}>{t("candidates.reject")}</Button>
                <Button variant="primary" loading={submitting} onClick={handleApprove}>{t("candidates.approve")}</Button>
              </SpaceBetween>
            </Box>
          ) : (
            <Box float="right"><Button onClick={() => setDetailItem(null)}>{t("candidates.close")}</Button></Box>
          )
        }
      >
        {detailItem && (
          <SpaceBetween size="l">
            <div style={{ background: "#f2f8fd", borderRadius: 8, padding: 16 }}>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13 }}>
                <div><span style={{ color: "#5f6b7a" }}>{t("candidates.evaluatorLabel")}</span> <strong>{detailItem.source_evaluator}</strong></div>
                <div><span style={{ color: "#5f6b7a" }}>{t("candidates.confidenceLabel")}</span> <strong>{(parseFloat(detailItem.confidence || "0") * 100).toFixed(0)}%</strong></div>
                <div><span style={{ color: "#5f6b7a" }}>{t("candidates.sessionLabel")}</span> <code style={{ fontSize: 12 }}>{(detailItem.source_session_id || "").slice(0, 30)}...</code></div>
                <div><span style={{ color: "#5f6b7a" }}>{t("candidates.dateLabel")}</span> {(detailItem.created_at || "").slice(0, 10)}</div>
              </div>
            </div>

            <div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{t("candidates.rootCause")}</div>
              <div style={{ fontSize: 14, color: "#414d5c", lineHeight: 1.6, background: "#fef3f2", borderRadius: 8, padding: 12, border: "1px solid #fecaca" }}>
                {detailItem.root_cause || t("candidates.noRootCause")}
              </div>
            </div>

            {evidenceUrl && (
              <div>
                <Link href={evidenceUrl} external>{t("candidates.evidence")}</Link>
              </div>
            )}

            {detailItem.status === "pending" && (
              <div style={{ borderTop: "1px solid #e9ebed", paddingTop: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>
                  {t("candidates.fieldsTitle")}
                </div>
                <SpaceBetween size="m">
                  <FormField label={t("candidates.inputPrompt")}>
                    <Textarea value={editPrompt} onChange={({ detail }) => setEditPrompt(detail.value)} rows={3} />
                  </FormField>
                  <FormField label={t("candidates.expectedBehavior")}>
                    <Textarea value={editExpected} onChange={({ detail }) => setEditExpected(detail.value)} rows={3} />
                  </FormField>
                  <FormField label={t("candidates.col.suite")}>
                    <Input value={editSuite} onChange={({ detail }) => setEditSuite(detail.value)} />
                  </FormField>
                  <FormField label={t("candidates.metrics")}>
                    <Input value={editMetrics} onChange={({ detail }) => setEditMetrics(detail.value)} />
                  </FormField>
                </SpaceBetween>
              </div>
            )}
          </SpaceBetween>
        )}
      </Modal>

      {toast && (
        <div style={{ position: "fixed", bottom: 24, right: 24, padding: "12px 20px", borderRadius: 8, fontSize: 14, fontWeight: 500, color: "#fff", background: toast.type === "success" ? "#00802f" : "#db0000", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", zIndex: 9999 }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
